import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { parseLocaleXml } from './parseLocaleXml';

const STORAGE_KEY = 'solvetax_locale';

function readStoredLocale() {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'en' || v === 'te' ? v : null;
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const stored = typeof window !== 'undefined' ? readStoredLocale() : null;
  const [showGate, setShowGate] = useState(() => stored == null);
  const [locale, setLocale] = useState(() => stored || 'en');
  const [strings, setStrings] = useState({});
  const [loadingStrings, setLoadingStrings] = useState(() => stored != null);

  const loadBundle = useCallback(async (loc) => {
    setLoadingStrings(true);
    try {
      const base = (typeof process !== 'undefined' && process.env.PUBLIC_URL) || '';
      const res = await fetch(`${base}/locales/${loc}.xml`);
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      setStrings(parseLocaleXml(text));
    } catch {
      setStrings({});
    } finally {
      setLoadingStrings(false);
    }
  }, []);

  useEffect(() => {
    if (showGate) return;
    loadBundle(locale);
  }, [locale, showGate, loadBundle]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (showGate) {
      document.documentElement.lang = 'en';
      return;
    }
    document.documentElement.lang = locale === 'te' ? 'te' : 'en';
  }, [locale, showGate]);

  const completeGate = useCallback((loc) => {
    const next = loc === 'te' ? 'te' : 'en';
    localStorage.setItem(STORAGE_KEY, next);
    setLocale(next);
    setShowGate(false);
    setLoadingStrings(true);
  }, []);

  const skipGate = useCallback(() => {
    completeGate('en');
  }, [completeGate]);

  const setLocalePreference = useCallback((loc) => {
    if (loc !== 'en' && loc !== 'te') return;
    localStorage.setItem(STORAGE_KEY, loc);
    setLoadingStrings(true);
    setLocale(loc);
  }, []);

  const t = useCallback((key, fallback = '', vars) => {
    let s = strings[key];
    if (s == null || s === '') s = fallback || key;
    if (vars && typeof vars === 'object') {
      Object.entries(vars).forEach(([k, v]) => {
        s = String(s).split(`{${k}}`).join(String(v));
      });
    }
    return s;
  }, [strings]);

  const value = useMemo(() => ({
    locale,
    showGate,
    loadingStrings: !showGate && loadingStrings,
    stringsReady: !showGate && !loadingStrings,
    completeGate,
    skipGate,
    setLocalePreference,
    t,
  }), [locale, showGate, loadingStrings, completeGate, skipGate, setLocalePreference, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
